const API_BASE_URL = 'https://domain-assistant-api.growaton.xyz';
//const API_BASE_URL = 'http://localhost:8080';

export interface PhraseAnalysis {
  id: number;
  phrase: string;
  sentiment: string;
  pos: number;
  neg: number;
  neu: number;
  created_at: string;
  updated_at: string;
  prompts?: Array<{
    role: string;
    content: string;
  }>;
  tags?: Array<{
    tagCategory: string;
    associatedTerms: string[];
  }>;
}

export interface Portfolio {
  id: number;
  phrase_analysis_id: number;
  name: string;
  description: string;
  keywords_explosion: string[];
  created_at: string;
  updated_at: string;
  phrase_analysis: PhraseAnalysis;
  domains_count: number;
}

export interface DomainHistory {
  id: number;
  domain_id: number;
  vendor_id: number;
  event_type: string;
  event_date: string | null;
  owner: string | null;
  status: string | null;
  registrar_id: string | null;
  domain_created_at: string | null;
  domain_updated_at: string | null;
  domain_expires_at: string | null;
  privacy_enabled: boolean | null;
  is_locked: boolean;
  is_disabled: boolean;
  is_for_sale: boolean | null;
  estimated_domain_age: number | null;
  registry_domain_id: string | null;
  dnssec_status: string | null;
  udrp_locked: boolean | null;
  registrant_unverified: boolean | null;
  renewal_option: string | null;
  note: string | null;
  raw_data: any;
  created_at: string;
  vendor_name: string;
  is_registrar: boolean;
  is_whois_provider: boolean;
}

export interface Domain {
  id: number;
  domain_name: string;
  description: string;
  tld: string;
  source: string;
  purchase_price: number;
  renewal_price: number;
  transfer_price: number;
  current_history_id: number | null;
  created_at: string;
  updated_at: string;
  history: DomainHistory[];
  nameservers: any[];
  contacts: any[];
  reputation: any[];
  security_tests: any[];
  security_warnings: any[];
  domain_analysis: any | null;
  portfolios: any[];
}

export interface PortfolioDetail {
  portfolio: Portfolio;
  phrase_analysis: PhraseAnalysis;
  domains: Domain[];
}

export interface PortfoliosResponse {
  success: boolean;
  count: number;
  portfolios: Portfolio[];
}

export interface PortfolioDetailResponse {
  success: boolean;
  portfolio_id: number;
  data: PortfolioDetail;
}

export interface DomainContact {
  id: number;
  history_id: number;
  contact_type: string;
  first_name: string;
  last_name: string;
  organization: string;
  address1: string;
  address2: string | null;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  country_code: string;
  phone: string;
  email: string;
  contact_id: string | null;
  registry_contact_id: string | null;
  created_at: string;
  event_date: string;
  event_type: string;
  vendor_name: string;
}

export interface DomainNameserver {
  id: number;
  history_id: number;
  hostname: string;
  ip_address: string | null;
  type: string;
  with_ads: boolean;
  created_at: string;
  event_date: string;
  event_type: string;
  vendor_name: string;
}

export interface DomainReputation {
  id: number;
  history_id: number;
  overall_score: number;
  mail_score: number;
  security_score: number;
  spam_score: number;
  last_checked_at: string;
  created_at: string;
  updated_at: string;
  event_date: string;
  event_type: string;
  vendor_name: string;
}

export interface DomainSecurityTest {
  id: number;
  history_id: number;
  test_type: string;
  test_code: number;
  test_name: string;
  score: number;
  performed_at: string;
  created_at: string;
  event_date: string;
  event_type: string;
  vendor_name: string;
}

export interface DomainHealthData {
  domain: {
    id: number;
    domain_name: string;
    tld: string;
    current_history_id: number;
    domain_created_at: string;
    domain_updated_at: string;
    source: string;
  };
  history: DomainHistory[];
  nameservers: DomainNameserver[];
  contacts: DomainContact[];
  reputation: DomainReputation[];
  security_tests: DomainSecurityTest[];
  security_warnings: any[];
  domain_analysis: any | null;
  word_segments: any[];
  word_relationships: any[];
  portfolios: any[];
}

export interface DomainHealthResponse {
  success: boolean;
  domain: string;
  data: DomainHealthData;
}

export interface TagGenerationPrompt {
  role: string;
  content: string;
}

export interface TagGenerationResponse {
  prompts: TagGenerationPrompt[];
  needAdditionalContext: boolean;
  nextQuestion: string;
  explanationOfNextQuestion: string;
  tags: Array<{
    tagCategory: string;
    associatedTerms: string[];
  }>;
  sentimentAnalysis?: {
    sentiment: string;
    pos: number;
    neg: number;
    neu: number;
  };
  nextStepAPI?: string;
  phraseId?: number;
}

export interface TagGenerationRequest {
  prompts: TagGenerationPrompt[];
  phraseId?: number;
}

export interface GeneratedDomain {
  domain: string;
  keywords: string[];
  description: string;
  source: string;
  purchase_price: number;
  transfer_price: number;
  renewal_price: number;
}

export interface DomainGenerationRequest {
  phraseId: number;
  prompts: TagGenerationPrompt[];
}

export interface DomainGenerationResponse {
  result: GeneratedDomain[];
}

class ApiService {
  private async request<T>(endpoint: string, options?: RequestInit, timeout?: number): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = timeout ? setTimeout(() => controller.abort(), timeout) : null;
    
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true',
        ...options?.headers,
      },
      signal: controller.signal,
      ...options,
    };

    try {
      const response = await fetch(url, config);
      
      // Clear timeout if request completes successfully
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      // Clear timeout on error
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error(`Request timeout after ${timeout}ms`);
      }
      
      console.error('API request failed:', error);
      throw error;
    }
  }

  async getPortfolios(): Promise<PortfoliosResponse> {
    return this.request<PortfoliosResponse>('/portfolios', undefined, 30000); // 30 seconds
  }

  async getPortfolioDetail(id: number): Promise<PortfolioDetailResponse> {
    return this.request<PortfolioDetailResponse>(`/portfolios/${id}`, undefined, 30000); // 30 seconds
  }

  async getDomainHealth(domainName: string): Promise<DomainHealthResponse> {
    return this.request<DomainHealthResponse>(`/domains/${domainName}`, undefined, 45000); // 45 seconds for domain health checks
  }

  async generateTags(request: TagGenerationRequest): Promise<TagGenerationResponse> {
    return this.request<TagGenerationResponse>('/tags-generation', {
      method: 'POST',
      body: JSON.stringify(request),
    }, 600000); // 10 minutes for domain generation
  }

  async generateDomains(request: DomainGenerationRequest): Promise<DomainGenerationResponse> {
    return this.request<DomainGenerationResponse>('/domains-generation', {
      method: 'POST',
      body: JSON.stringify(request),
    }, 600000); // 10 minutes for domain generation
  }
}

export const apiService = new ApiService(); 